import type { AuthorBlok } from "./AuthorStructuredDataSchema";
import {
  buildGraphPersonNode,
  buildWebPageAuthorRef,
} from "./AuthorStructuredDataSchema";
import {
  buildWebsiteSearchAction,
  type WebsiteSearchActionBlok,
} from "./WebsiteStructuredDataHelpers";

type OrganizationBlok = {
  name?: string;
  logo?: { filename?: string };
};

type WebsiteBlok = WebsiteSearchActionBlok & {
  name?: string;
};

type HeaderContent = {
  organization?: OrganizationBlok[];
  website?: WebsiteBlok[];
};

type PageContent = {
  og_title?: string;
  og_description?: string;
};

type StoryMeta = {
  first_published_at?: string;
  published_at?: string;
};

const toIsoDate = (value: string | undefined) => {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const buildOrganizationNode = (
  orgBlok: OrganizationBlok | undefined,
  baseURL: string
) => ({
  "@type": "Organization",
  "@id": `${baseURL}/#organization`,
  url: baseURL,
  ...(orgBlok?.name && { name: orgBlok.name }),
  ...(orgBlok?.logo?.filename && {
    logo: {
      "@type": "ImageObject",
      url: orgBlok.logo.filename,
    },
  }),
});

const buildWebsiteNode = (
  websiteBlok: WebsiteBlok | undefined,
  baseURL: string
) => {
  const searchAction = buildWebsiteSearchAction(websiteBlok?.search_action);

  return {
    "@type": "WebSite",
    "@id": `${baseURL}/#website`,
    url: baseURL,
    ...(websiteBlok?.name && { name: websiteBlok.name }),
    publisher: {
      "@id": `${baseURL}/#organization`,
    },
    ...(searchAction && { potentialAction: searchAction }),
  };
};

const buildWebPageNode = (
  pageContent: PageContent | null | undefined,
  storyMeta: StoryMeta | null | undefined,
  pageUrl: string,
  baseURL: string,
  authors?: AuthorBlok[]
) => {
  const datePublished = toIsoDate(storyMeta?.first_published_at);
  const dateModified = toIsoDate(storyMeta?.published_at);
  const author = authors?.length
    ? buildWebPageAuthorRef(authors, baseURL)
    : null;

  return {
    "@type": "WebPage",
    "@id": `${pageUrl}#webpage`,
    url: pageUrl,
    ...(pageContent?.og_title && { name: pageContent.og_title }),
    ...(pageContent?.og_description && { description: pageContent.og_description }),
    isPartOf: {
      "@id": `${baseURL}/#website`,
    },
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    publisher: {
      "@id": `${baseURL}/#organization`,
    },
    ...(author && { author }),
  };
};

export const buildWebPageStructuredData = (
  headerContent: HeaderContent | null | undefined,
  pageContent: PageContent | null | undefined,
  storyMeta: StoryMeta | null | undefined,
  pageUrl: string,
  baseURL: string,
  authors?: AuthorBlok[]
) => {
  const orgBlok = headerContent?.organization?.[0];
  const websiteBlok = headerContent?.website?.[0];
  const activeAuthors = authors ?? [];

  return {
    "@context": "https://schema.org",
    "@graph": [
      buildOrganizationNode(orgBlok, baseURL),
      buildWebsiteNode(websiteBlok, baseURL),
      ...activeAuthors.map((author) => buildGraphPersonNode(author, baseURL)),
      buildWebPageNode(pageContent, storyMeta, pageUrl, baseURL, activeAuthors),
    ],
  };
};
